// ============================================================================
// FACET NODE - Graph visualization node for extracted facets
// Distinctive diamond shape with amber coloring to differentiate from entities
// Shows facet type, content preview, and confidence level
// ============================================================================

import { memo } from 'react'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { FACET_TYPE_CONFIG, type FacetType, type FacetCategory } from '@/types/interviewer'

// ============================================================================
// TYPES
// ============================================================================

export interface FacetNodeData {
  uri: string
  facetType: string
  content: string
  confidence: number
  sourceTitle?: string
  entityCount?: number // Number of entities this facet describes
  status?: string
}

// ============================================================================
// CATEGORY COLORS - Matching the facet category system
// ============================================================================

const categoryColors: Record<FacetCategory, { bg: string; border: string; text: string; accent: string }> = {
  market: {
    bg: '#f0f7f2',
    border: '#5c8a6b',
    text: '#3d5c47',
    accent: '#5c8a6b',
  },
  business: {
    bg: '#eef5f8',
    border: '#4a90a4',
    text: '#2d5c6b',
    accent: '#4a90a4',
  },
  solution: {
    bg: '#fdf8ec',
    border: '#c9a227',
    text: '#8a7019',
    accent: '#c9a227',
  },
  insights: {
    bg: '#f5f5f5',
    border: '#6c757d',
    text: '#495057',
    accent: '#6c757d',
  },
}

// Default amber for unknown facet types
const defaultColors = {
  bg: '#fef7e6',
  border: '#d4a012',
  text: '#8a6914',
  accent: '#d4a012',
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getFacetColors(facetType: string): typeof defaultColors {
  const config = FACET_TYPE_CONFIG[facetType as FacetType]
  if (config) {
    return categoryColors[config.category]
  }
  return defaultColors
}

function getFacetLabel(facetType: string): string {
  const config = FACET_TYPE_CONFIG[facetType as FacetType]
  return config?.label || facetType
}

function truncateContent(content: string, maxLength: number): string {
  if (content.length <= maxLength) return content
  return content.substring(0, maxLength - 3) + '...'
}

// ============================================================================
// FACET NODE COMPONENT
// ============================================================================

export const FacetNode = memo(({ data, selected }: NodeProps & { data: FacetNodeData }) => {
  const colors = getFacetColors(data.facetType)
  const label = getFacetLabel(data.facetType)
  const hasEntities = data.entityCount !== undefined && data.entityCount > 0

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: colors.accent, width: 6, height: 6 }}
      />

      {/* Diamond shape container */}
      <div
        style={{
          padding: '8px 12px',
          borderRadius: '8px',
          border: `2px solid ${colors.border}`,
          backgroundColor: colors.bg,
          minWidth: '100px',
          maxWidth: '160px',
          boxShadow: selected
            ? `0 0 0 3px ${colors.border}40`
            : '0 1px 4px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.2s, transform 0.2s',
          cursor: 'pointer',
          position: 'relative',
          // Subtle diamond-like appearance with clipped corners
          clipPath: 'polygon(8px 0, calc(100% - 8px) 0, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px)',
        }}
      >
        {/* Entity count badge */}
        {hasEntities && (
          <div
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              backgroundColor: colors.accent,
              color: 'white',
              fontSize: '9px',
              fontWeight: 600,
              minWidth: '16px',
              height: '16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }}
          >
            {data.entityCount}
          </div>
        )}

        {/* Facet type label */}
        <div
          style={{
            fontSize: '10px',
            fontWeight: 600,
            color: colors.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px',
          }}
        >
          {label}
        </div>

        {/* Content preview */}
        <div
          style={{
            fontSize: '11px',
            color: colors.text,
            lineHeight: '1.3',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {truncateContent(data.content, 60)}
        </div>

        {/* Confidence indicator */}
        <div
          style={{
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <div
            style={{
              flex: 1,
              height: '3px',
              backgroundColor: `${colors.border}30`,
              borderRadius: '2px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.round(data.confidence * 100)}%`,
                height: '100%',
                backgroundColor: colors.accent,
                borderRadius: '2px',
              }}
            />
          </div>
          <span
            style={{
              fontSize: '9px',
              color: '#847561',
              fontFamily: 'monospace',
            }}
          >
            {Math.round(data.confidence * 100)}%
          </span>
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        style={{ background: colors.accent, width: 6, height: 6 }}
      />
    </>
  )
})

FacetNode.displayName = 'FacetNode'

export default FacetNode
